import React, { useEffect, useRef, useState } from 'react';
import { IoMdClose } from 'react-icons/io';
import { IoSearch } from 'react-icons/io5';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import axios from 'axios';
import './ChangePasswordAdminModal.css';
import { toast } from 'react-toastify';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const ChangePasswordAdminModal = ({ closeModal }) => {
  const [form, setForm] = useState({
    employeeId: '',
    name: '',
    branch: '',
    role: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  
  const [showOfficerDropdown, setShowOfficerDropdown] = useState(false);
  const [officerSearch, setOfficerSearch] = useState('');
  const [filteredOfficers, setFilteredOfficers] = useState([]);
  const [branchOfficers, setBranchOfficers] = useState([]);
  const [suppressDropdown, setSuppressDropdown] = useState(false);
  const [selectedOfficer, setSelectedOfficer] = useState(null);

  const dropdownRef = useRef(null);

  const getToken = () => {
    try {
      const raw = sessionStorage.getItem('tokens') || sessionStorage.getItem('token') || '';
      if (!raw) return '';
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          const t = parsed.token || parsed.accessToken || parsed.access_token || parsed.refreshToken || '';
          if (typeof t === 'string' && t.trim())
            return t.trim().replace(/^Bearer\s+/i, '');
        }
      } catch {}
      return String(raw).trim().replace(/^Bearer\s+/i, '');
    } catch (e) {
      console.error('getToken error', e);
      return '';
    }
  };

  const looksLikeJwt = (t) => {
    if (!t || typeof t !== 'string') return false;
    const parts = t.split('.');
    return parts.length === 3 && parts.every(Boolean);
  };

  const ensureTokenOrSetError = () => {
    const token = getToken();
    if (!token) {
      setGlobalError('Authentication token not found. Please login.');
      return null;
    }
    if (!looksLikeJwt(token)) {
      setGlobalError('Stored token appears malformed. Please login again.');
      return null;
    }
    return token;
  };

  // Fetch HO officers on component mount
  useEffect(() => {
    const fetchHOOfficers = async () => {
      const token = ensureTokenOrSetError();
      if (!token) return;

      try {
        const res = await axios.get(
          `${API_BASE}/officer/get-branch-officer-report?branch_code=HO&page=1&limit=1000&is_download=false`,
          {
            headers: { Authorization: `Bearer ${token}`, accept: '*/*' },
          }
        );

        if (res.data?.status && res.data?.data?.ho_officers?.length > 0) {
          const officers = res.data.data.ho_officers[0]?.officer_details || [];
          setBranchOfficers(officers);
        } else {
          setBranchOfficers([]);
        }
      } catch (err) {
        console.error('Failed to load HO officers', err);
        if (err.response?.status === 401) {
          setGlobalError('Authentication failed (invalid/expired token). Please login again.');
        } else {
          setGlobalError('Failed to load officers. Try again later.');
        }
      }
    };

    fetchHOOfficers();

  }, []);

  // Filter officers based on search
  useEffect(() => {
    const q = officerSearch.toLowerCase();
    setFilteredOfficers(
      branchOfficers.filter(
        (o) =>
          (o.emp_id || "").toLowerCase().includes(q) ||
          (o.first_name || "").toLowerCase().includes(q) ||
          (o.last_name || "").toLowerCase().includes(q) ||
          (o.email || "").toLowerCase().includes(q)
      )
    );

    if (suppressDropdown) {
      setShowOfficerDropdown(false);
      setSuppressDropdown(false);
      return;
    }

    if (officerSearch.trim() !== "") {
      setShowOfficerDropdown(true);
    } else {
      setShowOfficerDropdown(false);
    }
  }, [officerSearch, branchOfficers, suppressDropdown]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOfficerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const validate = (v) => {
    const e = {};
    if (!v.employeeId || !v.employeeId.trim()) e.employeeId = 'Employee ID is required';
    if (!v.name || !v.name.trim()) e.name = 'Name is required';
    if (!v.newPassword) e.newPassword = 'New password is required';
    else if (v.newPassword.length < 8)
      e.newPassword = 'Password must be at least 8 characters';
    else if (!/[A-Z]/.test(v.newPassword))
      e.newPassword = 'Must include at least 1 uppercase letter';
    else if (!/[0-9]/.test(v.newPassword))
      e.newPassword = 'Must include at least 1 number';
    else if (!/[!@#$%^&*(),.?":{}|<>_\-]/.test(v.newPassword))
      e.newPassword = 'Must include at least 1 special character';
    if (!v.confirmPassword) e.confirmPassword = 'Please confirm password';
    else if (v.confirmPassword !== v.newPassword)
      e.confirmPassword = 'Passwords do not match';
    return e;
  };

  const handleChange = (field) => (ev) => {
    const value = ev.target.value;
    setGlobalError('');
    setForm((p) => ({ ...p, [field]: value }));
    setErrors(validate({ ...form, [field]: value }));
  };

  const handleBlur = (field) => () => {
    setTouched((p) => ({ ...p, [field]: true }));
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();

    setTouched({
      employeeId: true,
      name: true,
      newPassword: true,
      confirmPassword: true,
    });

    const currentErrors = validate(form);
    if (Object.keys(currentErrors).length > 0) {
      setErrors(currentErrors);
      return;
    }

    const token = ensureTokenOrSetError();
    if (!token) return;

    setIsSaving(true);
    setGlobalError('');

    const payload = {
      emp_id: form.employeeId.trim(),
      branch_code: selectedOfficer?.branch_code || 'HO',
      full_name: form.name.trim(),
      role_code: selectedOfficer?.role_code || '',
      new_password: form.newPassword,
      confirm_password: form.confirmPassword,
    };

    try {
      const res = await axios.post(
        `${API_BASE}/officer/reset-password-via-admin`,
        payload,
        {
          headers: {
            accept: '*/*',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.data?.status) {
        toast.success('Password changed successfully');
        closeModal();
      } else {
        setGlobalError(res.data?.message || 'Failed to reset password');
        toast.error('Failed to reset password');
      }
    } catch (err) {
      console.error('Reset API error', err);
      setGlobalError(
        err.response?.data?.message ||
          (err.response?.status === 401
            ? 'Invalid/expired token. Please login again.'
            : 'Failed to reset password')
      );
      toast.error('Failed to reset password');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <div className="modal-overlay" onClick={closeModal}></div>
      <div className="add-user-modal admin-modal" role="dialog" aria-modal="true">
        <div className="modal-header">
          <h5>Change Password - Admin</h5>
          <button className="close-btn" onClick={closeModal} aria-label="Close">
            <IoMdClose />
          </button>
        </div>

        <form className="add-user-modal-content" onSubmit={handleSubmit}>
          {globalError && <div className="error-message">{globalError}</div>}

          {/* Search Employee ID */}
          <div className="form-group-row">
            <div className="branch-search-container">
              <label>Employee ID *</label>
              <div className="search-input-wrapper" ref={dropdownRef}>
                <input
                  type="text"
                  placeholder="Search employee ID..."
                  value={officerSearch}
                  onChange={(e) => setOfficerSearch(e.target.value)}
                  onFocus={() => officerSearch.trim() !== "" && setShowOfficerDropdown(true)}
                  className="branch-search-input"
                />
                <span className="search-icon-right">
                  <IoSearch />
                </span>

                {showOfficerDropdown && filteredOfficers.length > 0 && (
                  <div className="branch-dropdown-results">
                    {filteredOfficers.map((o) => (
                      <div
                        key={o.officer_id}
                        className="branch-dropdown-item"
                        onClick={() => {
                          setSuppressDropdown(true);
                          setSelectedOfficer(o);
                          setForm((p) => ({
                            ...p,
                            employeeId: o.emp_id,
                            name: `${o.first_name} ${o.last_name}`.trim(),
                            branch: o.branch_code,
                            role: o.role_code,
                          }));
                          setOfficerSearch(o.emp_id);
                          setShowOfficerDropdown(false);
                          setTimeout(() => {
                            if (document.activeElement instanceof HTMLElement) {
                              document.activeElement.blur();
                            }
                          }, 0);
                        }}
                      >
                        <div className="branch-item-name">
                          <strong>{o.emp_id}</strong> — {o.first_name} {o.last_name}
                        </div>
                        <div className="branch-item-code">
                          Role: {o.role_code}
                        </div>
                        <div className="branch-item-code">
                          Email: {o.email}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {touched.employeeId && errors.employeeId && (
                <div className="error-text">{errors.employeeId}</div>
              )}
            </div>
          </div>

          {/* Email ID - Auto-populated from selected officer */}
          <div className="form-group-row">
            <div className="admin-email-container">
              <label>Email ID</label>
              <div className="email-display-box">
                {selectedOfficer?.email || 'Select an employee to view email'}
              </div>
            </div>
          </div>

          {/* Name - Auto-populated, disabled for editing */}
          <div className="form-group-row">
            <div>
              <label>Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={handleChange('name')}
                onBlur={handleBlur('name')}
                placeholder="Admin name"
                disabled={true}
                className="auto-filled-field"
              />
              {selectedOfficer && (
                <small
                  style={{
                    color: '#059669',
                    fontSize: '0.85rem',
                    marginTop: '4px',
                    display: 'block',
                  }}
                >
                  ✓ Auto-filled from employee profile
                </small>
              )}
              {touched.name && errors.name && (
                <div className="error-text">{errors.name}</div>
              )}
            </div>
          </div>

          {/* Role - Auto-populated, disabled */}
          <div className="form-group-row">
            <div>
              <label>Role</label>
              <input
                type="text"
                value={form.role}
                disabled={true}
                className="auto-filled-field"
                placeholder="Role"
              />
              {selectedOfficer && (
                <small
                  style={{
                    color: '#059669',
                    fontSize: '0.85rem',
                    marginTop: '4px',
                    display: 'block',
                  }}
                >
                  ✓ Auto-filled from employee profile
                </small>
              )}
            </div>
          </div>

          {/* New Password */}
          <div className="form-group-row">
            <div className="password-wrapper">
              <label>New Password *</label>
              <div className="password-input-container">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={form.newPassword}
                  onChange={handleChange('newPassword')}
                  onBlur={handleBlur('newPassword')}
                  placeholder="Enter new password"
                  className="password-input"
                />
                <span
                  className="eye-icon"
                  onClick={() => setShowPassword((v) => !v)}
                  role="button"
                  tabIndex={0}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <AiOutlineEye /> : <AiOutlineEyeInvisible />}
                </span>
              </div>
              <small
                style={{
                  color: '#6b7280',
                  fontSize: '0.8rem',
                  display: 'block',
                  marginTop: '4px',
                }}
              >
                Must be different from current password
              </small>
              {touched.newPassword && errors.newPassword && (
                <div className="error-text">{errors.newPassword}</div>
              )}
            </div>
          </div>

          {/* Confirm Password */}
          <div className="form-group-row">
            <div className="password-wrapper">
              <label>Confirm Password *</label>
              <div className="password-input-container">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={form.confirmPassword}
                  onChange={handleChange('confirmPassword')}
                  onBlur={handleBlur('confirmPassword')}
                  placeholder="Re-enter password"
                  className="password-input"
                />
                <span
                  className="eye-icon"
                  onClick={() => setShowConfirm((v) => !v)}
                  role="button"
                  tabIndex={0}
                  aria-label={showConfirm ? 'Hide password' : 'Show password'}
                >
                  {showConfirm ? <AiOutlineEye /> : <AiOutlineEyeInvisible />}
                </span>
              </div>
              {touched.confirmPassword && errors.confirmPassword && (
                <div className="error-text">{errors.confirmPassword}</div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="modal-actions">
            <button type="submit" className="add-user-btn" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordAdminModal;